/**
 * The front desk's top bar (Reception C4): the building picker (own buildings
 * only), Online/Offline with the data's age and what waits to send, the printer
 * (the desk says, because a browser cannot see one). The bell beside it is the app's own.
 *
 * On a phone it shortens to icons with the words in aria-labels, so it fits one
 * row at 360.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BellIcon, BuildingOffice2Icon, CheckIcon, ChevronDownIcon, PrinterIcon, SignalIcon, SignalSlashIcon, XMarkIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import FormModal from '@/components/Common/FormModal';
import PendingQueuePanel from '@/components/Common/PendingQueuePanel';
import { usePendingQueue } from '@/components/Common/usePendingQueue';
import { useOnline } from '@/components/Tech/TechTopBar';
import { isLobbyDesk } from '@/config/receptionExperience';
import { useAuthStore } from '@/stores/authStore';
import { apiGet, apiPut } from '@/utils/api';
import { fmTime } from '@/utils/fmTime';
import { setDeskTimeZone } from '@/utils/timezone';
import { routeRequestError } from '@/utils/validation';
import { chooseDeskBuilding, useDeskBuilding, useDeskMe, type DeskBuilding } from './deskContext';
import { useDeskDataAge } from './deskApi';

const PILL = 'relative inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center gap-1 whitespace-nowrap rounded-full border px-2 text-sm font-medium sm:min-h-10 sm:px-2.5';

export function BuildingPicker() {
  const me = useDeskMe();
  const current = useDeskBuilding();
  const list = me.data?.buildings ?? [];
  const name = current?.name ?? (me.isLoading ? 'Loading' : 'No building');
  const words = `Building: ${name}`;
  return (
    <span className={`${PILL} border-line bg-surface text-content-secondary sm:max-w-[14rem]`} title={words}
          data-testid="desk-building-picker">
      <BuildingOffice2Icon className="h-5 w-5 shrink-0 sm:h-4 sm:w-4" aria-hidden="true" />
      <span className="hidden min-w-0 truncate sm:inline" data-testid="desk-building-name">{name}</span>
      {list.length > 1 && <ChevronDownIcon className="hidden h-3.5 w-3.5 shrink-0 sm:block" aria-hidden="true" />}
      {/* The real control, over the pill: the phone's own picker, the words read out. */}
      {list.length > 1 && (
        <select aria-label={`${words}. Choose a building.`} value={current?.id ?? ''} data-testid="desk-building-select"
                onChange={(e) => chooseDeskBuilding(e.target.value)}
                className="absolute inset-0 h-full w-full cursor-pointer opacity-0">
          {list.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
      )}
      {list.length <= 1 && <span className="sr-only">{words}</span>}
    </span>
  );
}

function ConnectionPill({ buildingId }: { buildingId: string | undefined }) {
  const online = useOnline();
  const { waiting, blocked } = usePendingQueue();
  const age = useDeskDataAge(buildingId);
  const [open, setOpen] = useState(false);
  const unsent = waiting.length + blocked.length;
  const dataFrom = !online && age ? fmTime(new Date(age.at), age.zone) : null;
  const Icon = online ? SignalIcon : SignalSlashIcon;
  const label = [
    online ? 'Online' : 'Offline',
    dataFrom ? `data from ${dataFrom}` : null,
    unsent > 0 ? `${unsent} ${unsent === 1 ? 'item' : 'items'} queued` : null,
  ].filter(Boolean).join(', ');
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid="desk-connection-pill" data-online={online ? 'yes' : 'no'}
              aria-label={`${label}. Open what is waiting on this device.`} title={label}
              className={`${PILL} ${online && blocked.length === 0
                ? 'border-success-200 bg-success-50 text-success-700' : 'border-warning-200 bg-warning-50 text-warning-800'}`}>
        <Icon className="h-5 w-5 shrink-0 sm:h-4 sm:w-4" aria-hidden="true" />
        <span className="hidden sm:inline">
          {online ? 'Online' : 'Offline'}
          {dataFrom && <span data-testid="desk-data-age"> · data from {dataFrom}</span>}
        </span>
        {unsent > 0 && (
          <span className="rounded-full bg-surface px-1.5 text-xs font-semibold" data-testid="desk-unsent">
            {unsent}<span className="hidden sm:inline"> queued</span>
          </span>
        )}
      </button>
      <PendingQueuePanel open={open} onClose={() => setOpen(false)} />
    </>
  );
}

function PrinterPill({ building }: { building: DeskBuilding }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(building.printer_name ?? '');
  const set = useMutation({
    networkMode: 'always',
    mutationFn: (ok: boolean) => apiPut('/reception/printer', { building_id: building.id, ok, name: name.trim() }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['reception', 'me'] });
      setOpen(false);
    },
    onError: (e) => { toast.error(routeRequestError(e, {})?.message ?? 'The printer status could not be changed.'); },
  });
  const words = building.printer_ok ? 'Printer ready' : 'Printer offline';
  const Mark = building.printer_ok ? CheckIcon : XMarkIcon;
  return (
    <>
      <button type="button" onClick={() => { setName(building.printer_name ?? ''); setOpen(true); }}
              data-testid="desk-printer-pill" data-ok={building.printer_ok ? 'yes' : 'no'}
              aria-label={`${words}${building.printer_name ? `: ${building.printer_name}` : ''}. Change the printer status.`}
              title={words}
              className={`${PILL} ${building.printer_ok
                ? 'border-success-200 bg-success-50 text-success-700' : 'border-danger-200 bg-danger-50 text-danger-700'}`}>
        <PrinterIcon className="h-5 w-5 shrink-0 sm:h-4 sm:w-4" aria-hidden="true" />
        <Mark className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        <span className="hidden lg:inline">{words}</span>
      </button>
      <FormModal isOpen={open} onClose={() => setOpen(false)} onSubmit={() => undefined} closeOnly title="Badge printer"
                 description="A browser can't see a printer, so you tell the desk. When it is off, the digital pass becomes the main button.">
        <label className="block text-sm font-medium text-content" htmlFor="desk-printer-name">Printer name (optional)</label>
        <input id="desk-printer-name" className="input-field mt-1" value={name} maxLength={120}
               onChange={(e) => setName(e.target.value)} />
        <div className="mt-4 flex flex-col gap-2 sm:flex-row">
          <button type="button" className="btn-primary min-h-[44px] flex-1" disabled={set.isPending}
                  data-testid="printer-ok" onClick={() => set.mutate(true)}>
            <CheckIcon className="h-5 w-5" aria-hidden="true" /> It is working
          </button>
          <button type="button" className="btn-secondary min-h-[44px] flex-1" disabled={set.isPending}
                  data-testid="printer-off" onClick={() => set.mutate(false)}>
            <XMarkIcon className="h-5 w-5" aria-hidden="true" /> It is not working
          </button>
        </div>
      </FormModal>
    </>
  );
}

export default function DeskTopBar() {
  // Set while rendering, before the page under it renders in the same pass. Only when
  // known: while the list loads, the person's own building (AppLayout) stands.
  const deskZone = useDeskBuilding()?.zone;
  if (deskZone) setDeskTimeZone(deskZone);
  const user = useAuthStore((s) => s.user);
  const building = useDeskBuilding();
  const { waiting: q1, blocked: q2 } = usePendingQueue();
  const unsentNow = q1.length + q2.length;
  // A lobby desk prints no badges (C33), so it has no printer to speak of.
  const lobby = isLobbyDesk(user);
  return (
    <div className="flex min-w-0 items-center gap-1 sm:gap-2" data-testid="desk-top-bar">
      <BuildingPicker />
      <ConnectionPill buildingId={building?.id} />
      {/* Queued work needs the room at 360; the printer pill is back when it's sent. */}
      {building && !lobby && <span className={unsentNow > 0 ? 'hidden sm:inline-flex' : 'inline-flex'}><PrinterPill building={building} /></span>}
    </div>
  );
}
